import type React from 'react';

import {
  CHAT_BLOCK,
  CHAT_MODE,
  COPILOT_WORKFLOWS,
} from '../../../shared/constants';
import {
  type ChatMessage,
  type ChatMode,
  type CombinedFormBlock,
  type CustomBlock,
  type ProgressBlock,
} from '../../../shared/types';
import { generateRandomString } from '../../../shared/utils';

export const getThreadId = (userId: string | number): string => {
  const epoch = Date.now();
  const rawId = `${userId}-${epoch}`;
  const encoded = btoa(rawId)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  return encoded;
};

export const decodeThreadId = (
  encodedId: string
): { userId: string | number; timestamp: number } => {
  const base64 = encodedId
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(encodedId.length + ((4 - (encodedId.length % 4)) % 4), '=');
  const decoded = atob(base64);
  const [userId, epochStr] = decoded.split('-');

  return {
    userId,
    timestamp: parseInt(epochStr, 10),
  };
};

export const getErrorBlock = (id: string, errorMessage: string) => ({
  id,
  mode: CHAT_MODE.ACTION,
  blocks: [
    {
      type: CHAT_BLOCK.ERROR,
      content:
        errorMessage === 'Error: AbortError'
          ? 'Stopped thinking'
          : errorMessage.replace('Error: ', ''),
    },
  ],
  metadata: {
    timestamp: new Date().toISOString(),
  },
});

export const getMarkdownBlock = (id: string, query: string) => ({
  id,
  mode: CHAT_MODE.USER,
  blocks: [
    {
      type: CHAT_BLOCK.MARKDOWN,
      content: query,
    },
  ],
  metadata: {
    timestamp: new Date().toISOString(),
  },
});

export const updateThread = (
  currentMessage: ChatMessage<ChatMode>,
  setterFn: React.Dispatch<React.SetStateAction<ChatMessage<ChatMode>[]>>
) => {
  const currentBlock = currentMessage.blocks[0];

  setterFn((prevThread) => {
    const isError = Boolean(currentBlock.type === CHAT_BLOCK.ERROR);

    const prevMessageId = `${currentMessage.id}-${prevThread.length - 1}`;
    const messageId = `${currentMessage.id}-${prevThread.length}`;
    const nextMessageId = `${currentMessage.id}-${prevThread.length + 1}`;

    const isEdited = Boolean(currentMessage.metadata.isEdited);
    const isCustomBlock = Boolean(
      currentMessage.mode === CHAT_MODE.USER &&
        currentMessage.blocks.length === 1 &&
        currentMessage.blocks[0].type === CHAT_BLOCK.CUSTOM
    );
    const isCombinedFormBlock = Boolean(
      currentMessage.mode === CHAT_MODE.USER &&
        currentMessage.blocks.length === 1 &&
        currentMessage.blocks[0].type === CHAT_BLOCK.COMBINEDFORM
    );

    // Blocks addition
    if (isError) {
      return [
        ...prevThread.slice(0, prevThread.length - 1),
        {
          ...currentMessage,
          blocks: currentMessage.blocks.map((b, idx) => ({
            ...b,
            blockId: `${messageId}-${idx}`,
          })),
          metadata: { ...currentMessage.metadata, messageId: prevMessageId },
        },
      ];
    }

    // Check if the step is edited step
    if (isEdited) {
      const matchIndex = prevThread.findIndex(
        (item) => currentMessage.metadata.actualKey === item.metadata.messageId
      );

      if (matchIndex === -1) {
        // If not found
        return [
          ...prevThread,
          // Current message
          {
            ...currentMessage,
            blocks: currentMessage.blocks.map((b, idx) => ({
              ...b,
              blockId: `${messageId}-${idx}`,
            })),
            metadata: {
              ...currentMessage.metadata,
              messageId,
              timestamp: new Date().toISOString(),
            },
          },
          // Progress block
          {
            id: currentMessage.id,
            mode: CHAT_MODE.ACTION,
            blocks: [
              {
                type: CHAT_BLOCK.PROGRESS,
                status: 0,
                content: '',
                blockId: `${messageId}-0`,
              },
            ],
            metadata: {
              messageId: nextMessageId,
              timestamp: new Date().toISOString(),
            },
          },
        ];
      }

      const prevMessage = prevThread[matchIndex];

      let prevBlock: ChatMessage<ChatMode> = prevMessage;

      if (isCustomBlock) {
        prevBlock = {
          ...prevMessage,
          blocks: prevMessage.blocks.map((b) => {
            if (b.blockId) {
              return {
                ...b,
                retentions: (currentBlock as CustomBlock).selections,
              } as typeof currentBlock;
            }

            return b;
          }),
          metadata: {
            ...prevMessage.metadata,
            isChanged: true,
            resetKey: generateRandomString(10),
          },
        };
      }

      if (isCombinedFormBlock) {
        prevBlock = {
          ...prevMessage,
          blocks: prevMessage.blocks.map((b) => {
            if (b.blockId) {
              return {
                ...b,
                retentions: {
                  customFormData: (currentBlock as CombinedFormBlock).custom
                    ?.formJson,
                  externalFormData: (currentBlock as CombinedFormBlock).external
                    ?.formData,
                },
              } as typeof currentBlock;
            }

            return b;
          }),
          metadata: {
            ...prevMessage.metadata,
            isChanged: true,
            resetKey: generateRandomString(10),
          },
        };
      }

      return [
        ...prevThread.slice(0, matchIndex),
        // Previous block
        prevBlock,
        // Current block
        {
          ...currentMessage,
          blocks: currentMessage.blocks.map((b, idx) => ({
            ...b,
            blockId: `${currentMessage.id}-${matchIndex + 1}-${idx}`,
          })),
          metadata: {
            ...currentMessage.metadata,
            messageId: `${currentMessage.id}-${matchIndex + 1}`,
            timestamp: new Date().toISOString(),
          },
        },
        // Progress block
        {
          id: currentMessage.id,
          mode: CHAT_MODE.ACTION,
          blocks: [
            {
              type: CHAT_BLOCK.PROGRESS,
              status: 0,
              content: '',
              blockId: `${currentMessage.id}-${matchIndex + 2}-0`,
            },
          ],
          metadata: {
            messageId: `${currentMessage.id}-${matchIndex + 2}`,
            timestamp: new Date().toISOString(),
          },
        },
      ];
    }

    // Retain selections for custom list
    if (isCustomBlock || isCombinedFormBlock) {
      const prevMessage = prevThread[prevThread.length - 1];

      let prevBlock: ChatMessage<ChatMode> = prevMessage;

      if (isCustomBlock) {
        prevBlock = {
          ...prevMessage,
          blocks: prevMessage.blocks.map((b) => {
            if (b.blockId === currentBlock.blockId) {
              return {
                ...b,
                retentions: (currentBlock as CustomBlock).selections,
              } as typeof currentBlock;
            }

            return b;
          }),
        };
      }

      if (isCombinedFormBlock) {
        prevBlock = {
          ...prevMessage,
          blocks: prevMessage.blocks.map((b) => {
            if (b.blockId === currentBlock.blockId) {
              return {
                ...b,
                retentions: {
                  customFormData: (currentBlock as CombinedFormBlock).custom
                    ?.formJson,
                  externalFormData: (currentBlock as CombinedFormBlock).external
                    ?.formData,
                },
              } as typeof currentBlock;
            }

            return b;
          }),
        };
      }

      return [
        ...prevThread.slice(0, prevThread.length - 1),
        // Previous block
        prevBlock,
        // Current block
        {
          ...currentMessage,
          blocks: currentMessage.blocks.map((b, idx) => ({
            ...b,
            blockId: `${messageId}-${idx}`,
            retentions: undefined,
          })),
          metadata: {
            ...currentMessage.metadata,
            messageId,
            timestamp: new Date().toISOString(),
          },
        },
        // Progress block
        {
          id: currentMessage.id,
          mode: CHAT_MODE.ACTION,
          blocks: [
            {
              type: CHAT_BLOCK.PROGRESS,
              status: 0,
              content: '',
              blockId: `${messageId}-0`,
            },
          ],
          metadata: {
            messageId: nextMessageId,
            timestamp: new Date().toISOString(),
          },
        },
      ];
    }

    return [
      ...prevThread,
      // Current block
      {
        ...currentMessage,
        blocks: currentMessage.blocks.map((b, idx) => ({
          ...b,
          blockId: `${messageId}-${idx}`,
        })),
        metadata: {
          ...currentMessage.metadata,
          messageId,
          timestamp: new Date().toISOString(),
        },
      },
      // Progress block
      {
        id: currentMessage.id,
        mode: CHAT_MODE.ACTION,
        blocks: [
          {
            type: CHAT_BLOCK.PROGRESS,
            status: 0,
            content: '',
            blockId: `${messageId}-0`,
          },
        ],
        metadata: {
          messageId: nextMessageId,
          timestamp: new Date().toISOString(),
        },
      },
    ];
  });

  return currentMessage;
};

export const appendThread = (
  nextMessage: ChatMessage<ChatMode>,
  setterFn: React.Dispatch<React.SetStateAction<ChatMessage<ChatMode>[]>>
) => {
  const nextBlock = nextMessage.blocks[0];

  // If still progress then update the last progress block
  if (nextBlock.type === CHAT_BLOCK.PROGRESS) {
    setterFn((prevThread) => {
      const prevMessage = prevThread[prevThread.length - 1];
      const typedLastProgressBlock = prevMessage.blocks[0] as ProgressBlock;
      const typedCurrentProgressBlock = nextBlock as ProgressBlock;
      const currentProgressText = `\n* ${typedCurrentProgressBlock.content} (${typedCurrentProgressBlock.status}%)`;

      return [
        ...prevThread.slice(0, prevThread.length - 1),
        {
          ...prevMessage,
          blocks: [
            {
              ...typedLastProgressBlock,
              content: typedLastProgressBlock.content + currentProgressText,
            },
          ],
        },
      ];
    });

    return;
  }

  const isWorkflowComplete =
    Boolean(nextMessage.metadata.workflowInfo?.isWorkflowComplete) ||
    !nextMessage.metadata.workflowInfo;

  // Update the progress block with the response block
  setterFn((prevThread) => {
    const prevMessageId = `${nextMessage.id}-${prevThread.length - 1}`;

    return [
      ...prevThread.slice(0, prevThread.length - 1).map((m) => ({
        ...m,
        metadata: {
          ...m.metadata,
          ...(isWorkflowComplete ? { isWfTillNowCompleted: true } : {}),
        },
      })),
      {
        ...nextMessage,
        blocks: nextMessage.blocks.map((b, idx) => ({
          ...b,
          blockId: `${prevMessageId}-${idx}`,
        })),
        metadata: {
          ...nextMessage.metadata,
          messageId: prevMessageId,
          ...(isWorkflowComplete ? { isWfTillNowCompleted: true } : {}),
        },
      },
    ];
  });
};

export const getWorkflowType = (
  workflowName: string | null | undefined
): string => {
  if (!workflowName) {
    return COPILOT_WORKFLOWS.DEFAULT.key;
  }

  // Service accounts
  if (workflowName === 'create_service_account_workflow') {
    return COPILOT_WORKFLOWS.RQ_SA_CREATE.key;
  }

  if (workflowName === 'modify_service_account_workflow') {
    return COPILOT_WORKFLOWS.RQ_SA_MODIFY.key;
  }

  if (workflowName === 'disable_service_account_workflow') {
    return COPILOT_WORKFLOWS.RQ_SA_DISABLE.key;
  }

  if (workflowName === 'enable_service_account_workflow') {
    return COPILOT_WORKFLOWS.RQ_SA_ENABLE.key;
  }

  if (workflowName === 'delete_service_account_workflow') {
    return COPILOT_WORKFLOWS.RQ_SA_DELETE.key;
  }

  if (workflowName === 'lock_service_account_workflow') {
    return COPILOT_WORKFLOWS.RQ_SA_LOCK.key;
  }

  if (workflowName === 'unlock_service_account_workflow') {
    return COPILOT_WORKFLOWS.RQ_SA_UNLOCK.key;
  }

  // Application requests
  if (workflowName === 'create_application_request_workflow') {
    return COPILOT_WORKFLOWS.RQ_AC_CREATE.key;
  }

  if (workflowName === 'modify_application_request_workflow') {
    return COPILOT_WORKFLOWS.RQ_AC_MODIFY.key;
  }

  if (workflowName === 'enable_application_request_workflow') {
    return COPILOT_WORKFLOWS.RQ_AC_ENABLE.key;
  }

  if (workflowName === 'disable_application_request_workflow') {
    return COPILOT_WORKFLOWS.RQ_AC_DISABLE.key;
  }

  if (workflowName === 'remove_application_request_workflow') {
    return COPILOT_WORKFLOWS.RQ_AC_REMOVE.key;
  }

  return COPILOT_WORKFLOWS.DEFAULT.key;
};
